<!--
  CompliancePage -- Distributor App Account: Compliance.

  Card: Distributor Onboarding & Account (https://trello.com/c/QV2U4KhF)

  Two stacked cards (the distributor default policy URLs were RETIRED on
  2026-10-19: each store owns its Privacy and Refund policy in the store
  editor's Tax & Legal section, as a URL or a hosted page):
    1. Master Services Agreement -- the ONE agreement (Jake, 2026-10-20; the
       separate "ThreadFlow Agreement" card is gone). FIRST, so an unsigned
       agreement is the first thing an Owner sees. Signed: the version, who
       confirmed it, their title and the date. Unsigned: a plain call-out
       and "Review and confirm", which opens /legal/agreement (the confirm
       sits at the foot of the text). A version bump re-prompts while the
       old signature stands, but only the CURRENT version lets a new store
       go live (F2, Jake 2026-10-21). The live-store backfill keeps its true
       state ("recorded as signed when your first store went live"), is never
       shown as a version, and says an Owner must confirm before a new store
       can go live.
    2. Sales Tax Nexus -- mounts the existing <TaxNexusEditor /> inline
       (it self-resolves the distributor id and owns its own load/save).

  ONE read, the same record the dashboard checklist and the store launch
  gate read: GET /api/distributors/<id>/agreement-acceptance. Nothing on
  this page writes the MSA.
-->
<template>
    <v-container
        fluid
        class="compliance-page"
        data-testid="dist-account-compliance"
    >
        <v-row>
            <v-col cols="12">
                <div class="d-flex align-center">
                    <h1 class="md-header">Compliance</h1>
                    <HelpIcon
                        audience="owner"
                        slug="set-up-your-business"
                        heading="compliance"
                    />
                </div>

                <section
                    class="compliance-card compliance-card--first"
                    data-testid="compliance-msa"
                >
                    <div class="d-flex align-center">
                        <h2 class="compliance-card__title">
                            Master Services Agreement
                            <span
                                v-if="signed"
                                class="compliance-msa__badge"
                                data-testid="compliance-msa-signed-badge"
                                >Signed</span
                            >
                        </h2>
                        <HelpIcon
                            audience="owner"
                            slug="set-up-your-business"
                            heading="confirm-your-master-services-agreement"
                        />
                    </div>

                    <template v-if="loaded && signed && backfilled">
                        <p class="compliance-card__sub" data-testid="compliance-msa-backfill-detail">
                            Recorded as signed when your first store went live
                            (recorded {{ formattedSignedAt }}).
                        </p>
                        <p class="compliance-card__sub" data-testid="compliance-msa-backfill-go-live">
                            An account Owner needs to review and confirm it here
                            before a new store can go live.
                        </p>
                    </template>
                    <template v-else-if="loaded && signed">
                        <p class="compliance-card__sub" data-testid="compliance-msa-detail">
                            Version {{ msaVersion }} &middot; confirmed
                            {{ confirmedOn }}<template v-if="acceptance">
                                by {{ acceptance.signer_name }},
                                {{ acceptance.signer_title }}</template>.
                        </p>
                        <!-- Disabled until a signed copy can be produced (e-sign). -->
                        <button
                            type="button"
                            class="tf-btn tf-btn--ghost"
                            disabled
                            data-testid="compliance-msa-download"
                        >
                            Download
                        </button>
                        <p
                            v-if="!confirmed"
                            class="compliance-card__sub"
                            data-testid="compliance-msa-new-version"
                        >
                            A new version ({{ currentVersion }}) needs your
                            review and confirmation before a new store can go
                            live.
                        </p>
                    </template>
                    <template v-else-if="loaded">
                        <v-alert
                            type="warning"
                            variant="tonal"
                            class="compliance-msa__callout"
                            data-testid="compliance-msa-unsigned-callout"
                        >
                            <strong>Master Services Agreement not signed</strong>
                            <span class="compliance-msa__callout-body">
                                Your stores cannot go live until an account
                                Owner reviews and confirms your ThreadFlow
                                Master Services Agreement.
                            </span>
                        </v-alert>
                        <p
                            class="compliance-card__sub"
                            data-testid="compliance-msa-empty"
                        >
                            Not yet signed.
                        </p>
                    </template>

                    <template v-if="loaded && !confirmed">
                        <router-link
                            v-if="canConfirm"
                            to="/legal/agreement"
                            class="tf-btn tf-btn--primary"
                            data-testid="compliance-msa-review"
                            >Review and confirm</router-link
                        >
                        <p
                            v-else
                            class="compliance-card__sub"
                            data-testid="compliance-msa-owner-note"
                        >
                            An account Owner must review and confirm it.
                        </p>
                    </template>
                </section>

                <section
                    class="compliance-card"
                    data-testid="dist-account-compliance-tax-nexus-card"
                >
                    <TaxNexusEditor />
                </section>

                <!--
                  Default terms text is HIDDEN: it is stored but nothing reads
                  it yet. Unhide with Post-MVP card "Store terms of service:
                  default terms text, inheritance and storefront rendering".
                -->
                <p
                    v-if="loadError"
                    class="compliance-error"
                    data-testid="compliance-load-error"
                >
                    Could not load your agreement status. Reload the page to
                    try again.
                </p>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { computed, ref, onMounted } from "vue";
import axios from "@/axios";
import { useAccountStore } from "@/stores/account";
import TaxNexusEditor from "@/pages/distributor/account/compliance/TaxNexusEditor.vue";
import HelpIcon from "@/components/Help/HelpIcon.vue";

// The MSA backfill's marker prefix (api/src/agreement_terms.py). The server
// already withholds it; this guard keeps the page honest if one ever slips
// through, because the marker is not a version name.
const MSA_BACKFILL_PREFIX = "backfill-";

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString();
}

export default {
    name: "CompliancePage",

    components: { HelpIcon, TaxNexusEditor },

    props: {
        // Explicit id for tests + admin surfaces; falls back to the Pinia
        // useAccountStore() current-tenant id in the live app.
        distributorId: {
            type: [Number, String],
            default: null,
        },
    },

    setup(props) {
        let resolvedId = props.distributorId;
        try {
            const account = useAccountStore();
            if (resolvedId === null || resolvedId === "") {
                resolvedId =
                    account.distributor && account.distributor.id
                        ? account.distributor.id
                        : null;
            }
        } catch (e) {
            if (resolvedId === "") resolvedId = null;
        }
        const distributorId = ref(resolvedId);

        const status = ref(null);
        const loaded = ref(false);
        const loadError = ref(false);

        const backfilled = computed(() => {
            const s = status.value || {};
            return (
                !!s.recorded_by_backfill ||
                (typeof s.msa_version === "string" &&
                    s.msa_version.startsWith(MSA_BACKFILL_PREFIX))
            );
        });
        const signed = computed(() => !!(status.value && status.value.signed));
        const confirmed = computed(() => !!(status.value && status.value.confirmed));
        const canConfirm = computed(() => !!(status.value && status.value.can_confirm));
        const msaVersion = computed(() =>
            backfilled.value ? null : (status.value && status.value.msa_version) || null
        );
        const currentVersion = computed(
            () => (status.value && status.value.current_version) || ""
        );
        const acceptance = computed(
            () => (status.value && status.value.acceptance) || null
        );
        const formattedSignedAt = computed(() =>
            formatDate(status.value && status.value.msa_signed_at)
        );
        const confirmedOn = computed(() =>
            formatDate(
                (acceptance.value && acceptance.value.accepted_at) ||
                    (status.value && status.value.msa_signed_at)
            )
        );

        async function load() {
            if (!distributorId.value) return;
            loadError.value = false;
            try {
                const resp = await axios.get(
                    `/api/distributors/${distributorId.value}/agreement-acceptance`
                );
                status.value = resp?.data || {};
                loaded.value = true;
            } catch (e) {
                loadError.value = true;
            }
        }

        onMounted(load);

        return {
            loaded,
            loadError,
            signed,
            confirmed,
            canConfirm,
            backfilled,
            msaVersion,
            currentVersion,
            acceptance,
            formattedSignedAt,
            confirmedOn,
        };
    },
};
</script>

<style scoped>
.compliance-page {
    max-width: 960px;
}
.compliance-card--first {
    margin-top: var(--tf-sp-4, 16px);
}
.compliance-msa__callout {
    margin-top: var(--tf-sp-3, 12px);
}
.compliance-msa__callout-body {
    display: block;
    margin-top: var(--tf-sp-1, 4px);
}
.compliance-msa__badge {
    margin-left: var(--tf-sp-2, 8px);
    padding: 2px var(--tf-sp-2, 8px);
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    vertical-align: middle;
    color: var(--tf-success-dark, #166534);
    background: var(--tf-success-light, #dcfce7);
}
.compliance-card {
    margin-top: var(--tf-sp-5, 24px);
    padding: var(--tf-sp-4, 16px);
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: var(--tf-radius-md, 8px);
    background: var(--tf-surface, #ffffff);
}
.compliance-card__title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    color: var(--tf-text, #1f2937);
}
.compliance-card__sub {
    margin-top: var(--tf-sp-2, 8px);
    color: var(--tf-text-muted, #4b5563);
    line-height: 1.5;
}
.compliance-error {
    margin-top: var(--tf-sp-3, 12px);
    color: var(--tf-error, #b91c1c);
    font-size: 0.875rem;
}
.tf-btn {
    display: inline-block;
    text-decoration: none;
    border: 1px solid transparent;
    border-radius: var(--tf-radius-sm, 6px);
    padding: var(--tf-sp-2, 8px) var(--tf-sp-5, 20px);
    font-size: var(--tf-size-body, 1rem);
    cursor: pointer;
}
.tf-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
.tf-btn--primary {
    margin-top: var(--tf-sp-3, 12px);
    background: var(--tf-primary, #1d4ed8);
    color: #ffffff;
}
.tf-btn--ghost {
    background: transparent;
    color: var(--tf-text, #1f2937);
    border-color: var(--tf-border, #d1d5db);
}
</style>
